import { useCallback, useEffect, useState } from "react";
import type { components } from "@/shared/lib/api.types";

// Absence payload carried into the tooltip and edit dialog (generated API shape).
export type CellAbsence = components["schemas"]["AbsenceCell"];

// One grid-wide tooltip shared by all cells. Caller keys it on absence id so it only remounts (replaying
// its animation) when the cursor crosses into a different absence.
export type AbsenceTooltipState = {
  absence: CellAbsence;
  x: number;
  y: number;
};

export function useAbsenceTooltip() {
  const [tooltip, setTooltip] = useState<AbsenceTooltipState | null>(null);

  const show = useCallback(
    (absence: CellAbsence, x: number, y: number) =>
      setTooltip({ absence, x, y }),
    [],
  );
  const hide = useCallback(() => setTooltip(null), []);

  // Hide when the pointer leaves any absence cell. `mouseover` fires only on boundary crossings (cheaper
  // than `mousemove`); keyed on active, not position, so it doesn't resubscribe.
  const active = tooltip !== null;
  useEffect(() => {
    if (!active) return;
    const onOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest("[data-absence-cell]")) hide();
    };
    document.addEventListener("mouseover", onOver);
    return () => document.removeEventListener("mouseover", onOver);
  }, [active, hide]);

  return { tooltip, show };
}
